import {useMemo, useState} from 'react'
import {Modal, Button, Form, Spinner} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {useIntl} from 'react-intl'
import {ASSIGNABLE_TARGETS} from '../../../gql/queries/twilioNumbers'
import {ASSIGN_PROVISIONED_NUMBER} from '../../../gql/mutations/twilioNumbers'
import {NUMBERS_MESSAGES as M} from '../numbersMessages'
import {TELEPHONY_MESSAGES as T} from '../../../helpers/telephonyMessages'

type Target = {id: number; name: string; email?: string}

type Props = {
  show: boolean
  onHide: () => void
  numberId: string | null
  numberLabel?: string
  currentAssignee?: string
  onAssigned?: () => void
}

// "Ada Lovelace · ada@example.com" — name and email are data, not copy. Hoisted
// out of JSX so the separator isn't a bare template literal in the tree.
const withEmail = (name: string, email?: string) => (email ? `${name} · ${email}` : name)

// Assign a number's routing to a user or a group (round-robin member).
// Billing stays with the owner; this only changes where it rings / dials out.
export const AssignNumberModal = ({show, onHide, numberId, numberLabel, currentAssignee, onAssigned}: Props) => {
  const intl = useIntl()
  const {data, loading} = useQuery(ASSIGNABLE_TARGETS, {fetchPolicy: 'cache-and-network', skip: !show})
  const [assign, {loading: assigning}] = useMutation(ASSIGN_PROVISIONED_NUMBER)

  const targets = data?.assignableTargets ?? {groups: [], users: []}
  const users: Target[] = targets.users ?? []
  const groups: Target[] = targets.groups ?? []

  const [mode, setMode] = useState<'user' | 'group'>('user')
  const [targetId, setTargetId] = useState('')

  const options = useMemo(() => (mode === 'user' ? users : groups), [mode, users, groups])

  const submit = async () => {
    if (!numberId || !targetId) {
      toast.warning(intl.formatMessage(M.toastPickDestination))
      return
    }
    try {
      const {data: res} = await assign({
        variables: {
          numberId,
          targetUserId: mode === 'user' ? targetId : null,
          targetGroupId: mode === 'group' ? targetId : null,
        },
      })
      const out = res?.assignProvisionedNumber
      if (out?.success) {
        toast.success(out.message || intl.formatMessage(M.toastAssigned))
        ;(out.warnings || []).forEach((w: string) => toast.warning(w))
        setTargetId('')
        onAssigned?.()
        onHide()
      }
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.toastAssignFailed))
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage(M.assignTitle, {
            number: numberLabel || intl.formatMessage(M.assignFallbackNumber),
          })}
          {currentAssignee ? (
            <div className='text-muted fs-7 fw-normal'>
              {intl.formatMessage(M.assignCurrently, {assignee: currentAssignee})}
            </div>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && !data ? (
          <div className='text-center py-4'>
            <Spinner animation='border' size='sm' />
          </div>
        ) : (
          <>
            <div className='mb-3 d-flex gap-4'>
              <Form.Check
                type='radio'
                id='assign-mode-user'
                label={intl.formatMessage(M.assignModeUser)}
                checked={mode === 'user'}
                onChange={() => {
                  setMode('user')
                  setTargetId('')
                }}
              />
              <Form.Check
                type='radio'
                id='assign-mode-group'
                label={intl.formatMessage(M.assignModeGroup)}
                checked={mode === 'group'}
                onChange={() => {
                  setMode('group')
                  setTargetId('')
                }}
              />
            </div>
            <Form.Select value={targetId} onChange={(e: any) => setTargetId(e.target.value)}>
              <option value=''>
                {intl.formatMessage(mode === 'user' ? M.assignSelectUser : M.assignSelectGroup)}
              </option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {withEmail(o.name, mode === 'user' ? o.email : undefined)}
                </option>
              ))}
            </Form.Select>
            {mode === 'group' && (
              <div className='text-muted fs-8 mt-2'>{intl.formatMessage(M.assignGroupHint)}</div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onHide} disabled={assigning}>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button variant='primary' onClick={submit} disabled={assigning || !targetId}>
          {assigning ? <Spinner animation='border' size='sm' /> : intl.formatMessage(T.assign)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
